import { ActionTree } from 'vuex';
import { EContentRequestType, IUpdateRequest } from './editor-ui.types';
import { EditorHelper } from '@/editor/helpers/editor.helper';
import {
  ICreateRowRequest,
  IDeleteRowRequest,
  IMoveCellRequest,
  IMoveNodesRequest,
  IMoveRowRequest,
  ISplitRowRequest,
  IUpdateCellsRequest,
  IUpdateNodeRequest,
} from '@/editor/api/services/types/requests';
import { IEditorBlock, ISelection } from '@/editor/types';
import { EContext, ENodeType, ERowType } from '@/editor/types/enums';
import {
  ISplitTextCreateRowAction,
  IUpdateNodeAction,
  ICreateRowAction,
  IDeleteNodeAction,
} from '@/editor/editor-block/store/types/actions';
import { RootState } from '@/store/store';
import { translate } from '@/helpers/translation.helper';
import {
  EEditorUiAction,
  editorUiNamespace,
  EEditorUiGetter,
} from '@/editor/editor-block/store/editor-ui.constants';
import {
  EEditorContentAction,
  EEditorContentGetter,
  EEditorContentMutation,
} from '@/editor/editor-block/store/editor-content.constants';
import { IEditorContentState } from '@/editor/editor-block/store/editor-content.types';

export const actions: ActionTree<IEditorContentState, RootState> = {
  [EEditorContentAction.ContentUpdateFailed]({ commit, dispatch }, block: IEditorBlock) {
    commit(EEditorContentMutation.ReplaceBlock, block);
    dispatch(EEditorContentAction.RevertDocumentPage, block);
    dispatch('alert/showDangerAlert', translate('we-could-not-save-last-edit-please-try-again'), {
      root: true,
    });
  },

  [EEditorContentAction.UpdateCells]({ commit, dispatch, state }, payload: IUpdateCellsRequest) {
    const blockBeforeUpdate: IEditorBlock = EditorHelper.getBlockById(state, payload.contentId);

    commit(EEditorContentMutation.UpdateCells, payload);
    dispatch(EEditorContentAction.UpdateDocumentPage, payload.contentId);
    const queueRequestPayload: IUpdateRequest = {
      contentId: payload.contentId,
      type: EContentRequestType.UpdateCells,
      payload: payload,
      blockBeforeUpdate,
    };
    dispatch(`${editorUiNamespace}/${EEditorUiAction.EnqueueRequest}`, queueRequestPayload, {
      root: true,
    });
  },

  [EEditorContentAction.CreateRow](
    { commit, dispatch, state },
    { rowType, nodePayload, ...basePayload }: ICreateRowAction
  ) {
    const blockBeforeUpdate: IEditorBlock = EditorHelper.getBlockById(state, basePayload.contentId);
    const createRowRequest: ICreateRowRequest = {
      ...basePayload,
      payload: EditorHelper.getRowPayload(rowType, nodePayload),
    };

    const selectionPayload: ISelection = {
      contentId: basePayload.contentId,
      sectionId: basePayload.targetSectionId,
      rowId: createRowRequest.payload.rowId,
      cellId: nodePayload ? createRowRequest.payload.cells[0].cellId : undefined,
      nodeId: nodePayload ? createRowRequest.payload.cells[0].nodes[0].nodeId : undefined,
    };

    commit(EEditorContentMutation.CreateRow, createRowRequest);
    dispatch(EEditorContentAction.UpdateDocumentPage, basePayload.contentId);
    dispatch(`${editorUiNamespace}/${EEditorUiAction.SetSelection}`, selectionPayload, {
      root: true,
    });

    const createRowRequestPayload = createRowRequest as ICreateRowRequest;
    createRowRequest.payload.cells.forEach(cell => cell.nodes.forEach(node => delete node.srcUrl));
    const queueRequestPayload: IUpdateRequest = {
      contentId: basePayload.contentId,
      type: EContentRequestType.CreateRow,
      payload: createRowRequestPayload,
      blockBeforeUpdate,
    };
    dispatch(`${editorUiNamespace}/${EEditorUiAction.EnqueueRequest}`, queueRequestPayload, {
      root: true,
    });
  },

  [EEditorContentAction.SplitTextCreateRow](
    { commit, dispatch, state },
    actionPayload: ISplitTextCreateRowAction
  ) {
    const {
      documentPageId,
      resourceId,
      contentId,
      targetSectionId,
      targetRowId,
      targetCellId,
      targetNodeId,
      firstNodeContent,
      firstNodeFields,
      lastNodeContent,
      lastNodeFields,
      nodePayload,
      rowType,
    } = actionPayload;
    const blockBeforeUpdate: IEditorBlock = EditorHelper.getBlockById(state, contentId);

    const updateFirstNodeRequest: IUpdateNodeRequest = {
      documentPageId,
      resourceId,
      contentId,
      targetSectionId,
      targetRowId,
      targetCellId,
      targetNodeId,
      payload: { content: firstNodeContent, fields: firstNodeFields },
    };

    const createRowRequest: ICreateRowRequest = {
      documentPageId,
      resourceId,
      contentId,
      targetSectionId,
      targetRowId,
      append: true,
      payload: EditorHelper.getRowPayload(rowType, nodePayload),
    };

    const createLastRowRequest: ICreateRowRequest = {
      documentPageId,
      resourceId,
      contentId,
      targetSectionId,
      targetRowId: createRowRequest.payload.rowId,
      append: true,
      payload: EditorHelper.getRowPayload(ERowType.Standard, {
        nodeType: ENodeType.Text,
        content: lastNodeContent,
        fields: lastNodeFields,
      }),
    };

    const moveNodesRequest: IMoveNodesRequest = {
      documentPageId,
      resourceId,
      contentId,
      sourceSectionId: targetSectionId,
      sourceRowId: targetRowId,
      sourceCellId: targetCellId,
      sourceNodeIds: EditorHelper.getNodeIdsToMove(state, actionPayload),
      targetSectionId,
      targetRowId: createLastRowRequest.payload.rowId,
      targetCellId: createLastRowRequest.payload.cells[0].cellId,
      targetNodeId: createLastRowRequest.payload.cells[0].nodes[0].nodeId,
    };

    const selectionPayload: ISelection = {
      contentId,
      sectionId: targetSectionId,
      rowId: createRowRequest.payload.rowId,
      cellId: nodePayload ? createRowRequest.payload.cells[0].cellId : undefined,
      nodeId: nodePayload ? createRowRequest.payload.cells[0].nodes[0].nodeId : undefined,
    };

    commit(EEditorContentMutation.UpdateNode, updateFirstNodeRequest);
    commit(EEditorContentMutation.CreateRow, createRowRequest);
    commit(EEditorContentMutation.CreateRow, createLastRowRequest);
    commit(EEditorContentMutation.MoveNodes, moveNodesRequest);
    dispatch(EEditorContentAction.UpdateDocumentPage, contentId);
    dispatch(`${editorUiNamespace}/${EEditorUiAction.SetSelection}`, selectionPayload, {
      root: true,
    });
    createRowRequest.payload.cells.forEach(cell => cell.nodes.forEach(node => delete node.srcUrl));
    const splitRowRequest: ISplitRowRequest = {
      documentPageId,
      contentId,
      resourceId,
      targetSectionId,
      targetRowId,
      targetCellId,
      targetNodeId,
      splitInput: updateFirstNodeRequest.payload,
      createInput: [createRowRequest.payload, createLastRowRequest.payload],
    };
    const queueRequestPayload: IUpdateRequest = {
      contentId,
      type: EContentRequestType.SplitTextCreateRow,
      payload: splitRowRequest,
      blockBeforeUpdate,
    };
    dispatch(`${editorUiNamespace}/${EEditorUiAction.EnqueueRequest}`, queueRequestPayload, {
      root: true,
    });
  },

  [EEditorContentAction.DeleteRow]({ commit, dispatch, state }, payload: IDeleteRowRequest) {
    const blockBeforeUpdate: IEditorBlock = EditorHelper.getBlockById(state, payload.contentId);

    commit(EEditorContentMutation.DeleteRow, payload);
    dispatch(EEditorContentAction.UpdateDocumentPage, payload.contentId);
    const queueRequestPayload: IUpdateRequest = {
      contentId: payload.contentId,
      type: EContentRequestType.DeleteRow,
      payload: payload,
      blockBeforeUpdate,
    };
    dispatch(`${editorUiNamespace}/${EEditorUiAction.EnqueueRequest}`, queueRequestPayload, {
      root: true,
    });
  },

  [EEditorContentAction.MoveRow]({ commit, dispatch, state }, payload: IMoveRowRequest) {
    const blockBeforeUpdate: IEditorBlock = EditorHelper.getBlockById(state, payload.contentId);

    commit(EEditorContentMutation.MoveRow, payload);
    dispatch(EEditorContentAction.UpdateDocumentPage, payload.contentId);
    const queueRequestPayload: IUpdateRequest = {
      contentId: payload.contentId,
      type: EContentRequestType.MoveRow,
      payload: payload,
      blockBeforeUpdate,
    };
    dispatch(`${editorUiNamespace}/${EEditorUiAction.EnqueueRequest}`, queueRequestPayload, {
      root: true,
    });
  },

  [EEditorContentAction.MoveNodes]({ commit, dispatch, state }, payload: IMoveNodesRequest) {
    const blockBeforeUpdate: IEditorBlock = EditorHelper.getBlockById(state, payload.contentId);

    commit(EEditorContentMutation.MoveNodes, payload);
    dispatch(EEditorContentAction.UpdateDocumentPage, payload.contentId);
    const queueRequestPayload: IUpdateRequest = {
      contentId: payload.contentId,
      type: EContentRequestType.MoveNodes,
      payload: payload,
      blockBeforeUpdate,
    };
    dispatch(`${editorUiNamespace}/${EEditorUiAction.EnqueueRequest}`, queueRequestPayload, {
      root: true,
    });
  },

  [EEditorContentAction.MoveCell]({ commit, dispatch, state }, payload: IMoveCellRequest) {
    const blockBeforeUpdate: IEditorBlock = EditorHelper.getBlockById(state, payload.contentId);

    commit(EEditorContentMutation.MoveCell, payload);
    dispatch(EEditorContentAction.UpdateDocumentPage, payload.contentId);
    const queueRequestPayload: IUpdateRequest = {
      contentId: payload.contentId,
      type: EContentRequestType.MoveCell,
      payload: payload,
      blockBeforeUpdate,
    };
    dispatch(`${editorUiNamespace}/${EEditorUiAction.EnqueueRequest}`, queueRequestPayload, {
      root: true,
    });
  },

  [EEditorContentAction.UpdateNode]({ commit, dispatch, state }, payload: IUpdateNodeAction) {
    const blockBeforeUpdate: IEditorBlock = EditorHelper.getBlockById(state, payload.contentId);

    commit(EEditorContentMutation.UpdateNode, payload);
    dispatch(EEditorContentAction.UpdateDocumentPage, payload.contentId);
    const updateNodeRequest = payload as IUpdateNodeRequest;

    delete updateNodeRequest.payload.srcUrl;
    const queueRequestPayload: IUpdateRequest = {
      contentId: payload.contentId,
      type: EContentRequestType.UpdateNode,
      payload: updateNodeRequest,
      blockBeforeUpdate,
    };
    dispatch(`${editorUiNamespace}/${EEditorUiAction.EnqueueRequest}`, queueRequestPayload, {
      root: true,
    });
  },

  [EEditorContentAction.UpdateDocumentPage]({ commit, rootGetters, getters }, targetId: string) {
    const context: EContext = rootGetters[`${editorUiNamespace}/${EEditorUiGetter.Context}`];
    const block: IEditorBlock = getters[EEditorContentGetter.BlockById](targetId);

    if (context === EContext.Document) {
      commit('dealroom/replaceEditorBlock', block, {
        root: true,
      });
    } else if (context === EContext.DocumentView) {
      commit('dealroomView/replaceEditorBlock', block, {
        root: true,
      });
    }
  },

  [EEditorContentAction.RevertDocumentPage]({ commit, rootGetters }, block: IEditorBlock) {
    const context: EContext = rootGetters[`${editorUiNamespace}/${EEditorUiGetter.Context}`];

    if (context === EContext.Document) {
      commit('dealroom/replaceEditorBlock', block, {
        root: true,
      });
    } else if (context === EContext.DocumentView) {
      commit('dealroomView/replaceEditorBlock', block, {
        root: true,
      });
    }
  },

  [EEditorContentAction.DeleteNode]({ commit, dispatch, state }, payload: IDeleteNodeAction) {
    const blockBeforeUpdate: IEditorBlock = EditorHelper.getBlockById(state, payload.contentId);

    commit(EEditorContentMutation.DeleteNode, payload);
    dispatch(EEditorContentAction.UpdateDocumentPage, payload.contentId);
    const queueRequestPayload: IUpdateRequest = {
      contentId: payload.contentId,
      type: EContentRequestType.DeleteNode,
      payload,
      blockBeforeUpdate,
    };
    dispatch(`${editorUiNamespace}/${EEditorUiAction.EnqueueRequest}`, queueRequestPayload, {
      root: true,
    });
  },

  [EEditorContentAction.SetBlock]({ commit }, block: IEditorBlock) {
    commit(EEditorContentMutation.SetBlock, block);
  },

  [EEditorContentAction.ResetBlocks]({ commit }) {
    commit(EEditorContentMutation.ResetBlocks);
  },
};
